import React from 'react';
import PropTypes from 'prop-types';
import { tokens, useBrsCreateStyles } from '../BrsCreateStyles';
import { DAYS, normalizeScheduleForMode, parseCellInputForMode } from './splitWeekUtils';

const rotationCellClass = (classes, value) => {
  if (value >= 75) return classes.rotationCellActive;
  if (value >= 40) return classes.rotationCellHalf;
  if (value > 0) return classes.rotationCellHalf;
  return classes.rotationCellEmpty;
};

export const WeeklyRotationSummary = ({
  spots,
  onSpotsChange,
  unitRotation,
  acknowledgedZeroDays,
  onToggleZeroDay,
}) => {
  const classes = useBrsCreateStyles();
  const safeSpots = Array.isArray(spots) ? spots : [];
  const hasSpots = safeSpots.length > 0;
  const editable = typeof onSpotsChange === 'function';

  // Pre-normalize every spot's schedule so cells and totals agree.
  const normalizedSpots = safeSpots.map((spot, idx) => ({
    key: spot?._id ?? idx,
    idx,
    isci: spot?.isciasset?.isci || `Spot ${idx + 1}`,
    title: spot?.title || 'Untitled',
    schedule: normalizeScheduleForMode(spot?.split_week_schedule, unitRotation),
  }));

  const dailyTotals = DAYS.reduce((acc, d) => {
    acc[d.key] = normalizedSpots.reduce((sum, s) => sum + s.schedule[d.key], 0);
    return acc;
  }, {});

  // In Rotation mode every day column must total exactly 0% or 100% across all
  // spots. Anything else — a partial split (1..99%) or a sum over 100% — is
  // hard-invalid and blocks submission. A 0-total day is allowed in *both*
  // modes but the operator must click that day's total to confirm (see
  // onToggleZeroDay) before submitting.
  const isRotation = unitRotation === 'rotation';
  const isUnit = unitRotation === 'unit';
  const isAcknowledged = (key) => acknowledgedZeroDays.includes(key);
  const dayInvalid = (total) => isRotation && total !== 0 && total !== 100;
  const anyDayInvalid = DAYS.some((d) => dayInvalid(dailyTotals[d.key]));
  // Zero-day confirmation applies to both Rotation and Unit modes.
  const zeroDays = DAYS.filter((d) => dailyTotals[d.key] === 0);
  const unconfirmedZeroDays = zeroDays.filter((d) => !isAcknowledged(d.key));
  const hasUnconfirmedZeroDays = unconfirmedZeroDays.length > 0;

  const handleCellEdit = (spotIdx, dayKey, rawValue) => {
    if (!editable) return;
    const parsed = parseCellInputForMode(rawValue, unitRotation);
    if (parsed === null) return;
    onSpotsChange((prev) => {
      if (!Array.isArray(prev)) return prev;
      const next = [...prev];
      const target = next[spotIdx];
      if (!target) return prev;
      const currentSchedule = normalizeScheduleForMode(target.split_week_schedule, unitRotation);
      const updatedSchedule = { ...currentSchedule, [dayKey]: parsed };
      const updatedSpot = { ...target, split_week_schedule: updatedSchedule };
      // If all days are no longer the same value, clear the unit/rotation field
      const vals = DAYS.map((d) => updatedSchedule[d.key]);
      if (!vals.every((v) => v === vals[0])) {
        updatedSpot.unit = '';
        updatedSpot.rotation = '';
      }
      next[spotIdx] = updatedSpot;
      return next;
    });
  };

  const renderHintBody = () => {
    if (anyDayInvalid) {
      return (
        <>
          <strong>Each day must total exactly 100%</strong> in Rotation mode. Adjust the
          split-week percentages so every day column sums to 100% (or 0% if nothing airs that
          day). Generate&nbsp;PDF and Submit&nbsp;Job stay disabled until then.
        </>
      );
    }
    if (hasUnconfirmedZeroDays) {
      const labels = unconfirmedZeroDays.map((d) => d.label).join(', ');
      return (
        <>
          <strong>{labels}</strong> {unconfirmedZeroDays.length > 1 ? 'have' : 'has'} a 0% daily
          total (nothing airing). Click the highlighted <strong>Daily Total</strong> to confirm
          before you can submit.
        </>
      );
    }
    return (
      <>
        Daily totals aggregate every spot&rsquo;s split-week schedule. Type an integer{' '}
        {isUnit ? '' : '0\u2013100 '}in any cell to change that spot&rsquo;s{' '}
        {isUnit ? 'unit count' : 'rotation'} for that day.
      </>
    );
  };

  return (
    <>
      <div className={classes.sectionHeader} style={{ marginTop: 20 }}>
        Weekly Rotation Summary
      </div>
      <div className={classes.card} style={{ overflowX: 'auto' }}>
        {hasSpots ? (
          <table className={classes.weeklyRotationTable}>
            <thead>
              <tr>
                <th>ISCI / Spot</th>
                {DAYS.map((d) => (
                  <th key={d.key}>{d.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {normalizedSpots.map((s) => (
                <tr key={s.key}>
                  <td>
                    <div className={classes.rotationSpotIsci}>{s.isci}</div>
                    <div className={classes.rotationSpotName}>{s.title}</div>
                  </td>
                  {DAYS.map((d) => {
                    const value = s.schedule[d.key];
                    return (
                      <td key={d.key}>
                        <span className={`${classes.rotationCell} ${rotationCellClass(classes, value)}`}>
                          {editable ? (
                            <input
                              className={classes.rotationCellInput}
                              type="number"
                              min="0"
                              {...(isUnit ? {} : { max: 100 })}
                              step="1"
                              value={value}
                              onChange={(e) => handleCellEdit(s.idx, d.key, e.target.value)}
                              aria-label={`${s.isci} ${d.label} ${isUnit ? 'unit count' : 'rotation percent'}`}
                            />
                          ) : (
                            <>{value > 0 ? (isUnit ? `${value}` : `${value}%`) : '—'}</>
                          )}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr style={{ borderTop: `2px solid ${tokens.accent}` }}>
                <td
                  style={{
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}>
                  Daily Total
                </td>
                {DAYS.map((d) => {
                  const total = dailyTotals[d.key];
                  const invalid = dayInvalid(total);
                  const isZeroDay = total === 0;
                  const acked = isZeroDay && isAcknowledged(d.key);
                  const clickable = isZeroDay && typeof onToggleZeroDay === 'function';

                  let style = {};
                  if (invalid) {
                    style = { background: 'rgba(255,92,108,0.18)', color: tokens.danger };
                  } else if (acked) {
                    style = { background: 'rgba(60,231,234,0.12)', color: tokens.info };
                  } else if (isZeroDay) {
                    style = { background: 'rgba(255,179,64,0.15)', color: tokens.warning };
                  }

                  let label;
                  if (isZeroDay) {
                    label = acked
                      ? (isUnit ? '0 ✓' : '0% ✓')
                      : (isUnit ? '0' : '0%');
                  } else {
                    label = total > 0 ? (isUnit ? `${total}` : `${total}%`) : '—';
                  }

                  return (
                    <td key={d.key}>
                      <span
                        className={`${classes.rotationCell} ${classes.rotationCellTotal}`}
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        onClick={clickable ? () => onToggleZeroDay(d.key) : undefined}
                        onKeyDown={
                          clickable
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onToggleZeroDay(d.key);
                                }
                              }
                            : undefined
                        }
                        title={
                          clickable
                            ? acked
                              ? 'Confirmed empty — click to undo'
                              : 'Click to confirm this day is intentionally empty (0%)'
                            : undefined
                        }
                        style={{ ...style, ...(clickable ? { cursor: 'pointer' } : {}) }}>
                        {label}
                      </span>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        ) : (
          <div className={classes.phasePlaceholder}>
            Add spots above to see the weekly rotation summary.
          </div>
        )}
        <div className={classes.cycleHint} style={{ marginTop: 12 }}>
          <span
            style={{
              color: anyDayInvalid
                ? tokens.danger
                : hasUnconfirmedZeroDays
                  ? tokens.warning
                  : tokens.info,
            }}>
            {anyDayInvalid || hasUnconfirmedZeroDays ? '⚠️' : '\u{1F4A1}'}
          </span>
          <span>{renderHintBody()}</span>
        </div>
      </div>
    </>
  );
};

WeeklyRotationSummary.propTypes = {
  spots: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      isciasset: PropTypes.shape({
        isci: PropTypes.string,
      }),
      split_week_schedule: PropTypes.object,
    }),
  ),
  onSpotsChange: PropTypes.func,
  unitRotation: PropTypes.string,
  acknowledgedZeroDays: PropTypes.arrayOf(PropTypes.string),
  onToggleZeroDay: PropTypes.func,
};

WeeklyRotationSummary.defaultProps = {
  spots: [],
  onSpotsChange: undefined,
  unitRotation: 'unit',
  acknowledgedZeroDays: [],
  onToggleZeroDay: undefined,
};
